// Deposit settlement: the refund arithmetic and the states the UI renders.
//
// Everything here is pure. It is shared by the admin card, the representative's
// release page, the bundle tick, and the refund statement generator, so all
// four always agree on what is owed — the same reason resolveDepositState in
// lib/rent.ts exists for the deposit itself.
//
// The deposit worked from is the amount ACTUALLY HELD: the sum of
// effectively-paid deposit rows, via resolveDepositState. It is never
// recalculated from current rent. The deposit is contractually fixed at
// signing (#767), and a tenant who paid EUR 675 must not have EUR 800 withheld
// because rent went up after they moved in.
//
// The database enforces the same rules in 20260814130000_deposit_settlements.sql
// and recomputes the refund at release. This module exists so the UI can show a
// running total and a validation error without a round trip — it is not the
// authority, and a disagreement between the two is a bug in this file.

export const DEDUCTION_CATEGORIES = [
  "damage",
  "cleaning",
  "unpaid_rent",
  "fees",
  "other",
] as const;

export type DeductionCategory = (typeof DEDUCTION_CATEGORIES)[number];

/**
 * Minimum reason length, mirroring the DB CHECK. Long enough to reject
 * "damage" and "x", short enough not to pretend it can judge prose.
 */
export const REASON_MIN_LENGTH = 10;

/**
 * Above this, the bundle is emailed as a signed link instead of an attachment.
 *
 * Resend accepts 40 MB, but base64 inflates payloads by about a third and
 * receiving mail servers commonly cap at 10-25 MB — so the ceiling is set by
 * the recipient, not by us. 8 MB of PDF is roughly 10.7 MB on the wire.
 */
export const BUNDLE_ATTACH_MAX_BYTES = 8 * 1024 * 1024;

/**
 * Private bucket holding bundles and refund statements. Must be read through
 * createSignedUrl — getPublicUrl returns a URL that 400s. Mirrors
 * BUNDLE_BUCKET in supabase/functions/_shared/moveout-bundle-parts.ts.
 */
export const BUNDLE_BUCKET = "deposit-settlements";

export type SettlementStatus =
  "draft" | "sent_to_lessor" | "released" | "closed";

export type SettlementDecision = "full" | "partial" | "none";

export interface DeductionInput {
  category?: DeductionCategory | string | null;
  amount_cents?: number | null;
  reason?: string | null;
}

/**
 * The settlement columns any surface may read. Deliberately all-optional: the
 * generated Supabase types do not cover these tables yet, and a database that
 * has not had the migration applied returns nothing at all.
 */
export interface SettlementRow {
  id?: string;
  status?: SettlementStatus | string | null;
  decision?: SettlementDecision | string | null;
  deposit_held_cents?: number | null;
  deposit_expected_cents?: number | null;
  amounts_snapshot_at?: string | null;
  refund_cents?: number | null;
  excess_cents?: number | null;
  bundle_recipient_email?: string | null;
  bundle_recipient_name?: string | null;
  bundle_pdf_path?: string | null;
  bundle_page_count?: number | null;
  bundle_size_bytes?: number | null;
  bundle_generated_at?: string | null;
  bundle_sent_at?: string | null;
  bundle_blocked_reason?: string | null;
  review_token_expires_at?: string | null;
  review_opened_at?: string | null;
  review_reminded_at?: string | null;
  released_at?: string | null;
  released_by_user_id?: string | null;
  released_by_name?: string | null;
  released_by_role?: string | null;
  release_note?: string | null;
  statement_pdf_path?: string | null;
  /** Set when the TENANT has the statement — not "someone does". */
  statement_sent_at?: string | null;
  statement_tenant_sent_at?: string | null;
  statement_rep_sent_at?: string | null;
  refund_paid_at?: string | null;
  refund_proof_path?: string | null;
  refund_payout_iban_last4?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

/* ══════════════════════════════════════════════════════════════════════════ */
/* refund arithmetic                                                          */
/* ══════════════════════════════════════════════════════════════════════════ */

export interface RefundMath {
  /** Sum of all deduction lines (cents). */
  deductionsCents: number;
  /** What goes back to the tenant (cents). Never negative. */
  refundCents: number;
  /**
   * Deductions beyond what is held (cents). Surfaced as a separate receivable
   * rather than netted off, so a tenant who owes more than their deposit stays
   * visible instead of being rounded away to a zero refund.
   */
  excessCents: number;
  /** Does the claim exceed the deposit? */
  overDeducted: boolean;
}

const cents = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : 0;
};

/** Total of the deduction lines, ignoring anything unparseable. */
export function sumDeductionCents(deductions: DeductionInput[]): number {
  return (deductions || []).reduce((sum, d) => {
    const amount = cents(d?.amount_cents);
    // Zero and negative lines cannot exist in the DB; if one arrives here it is
    // an unsaved draft row, and counting it would show a total the tenant's
    // statement will not match.
    return amount > 0 ? sum + amount : sum;
  }, 0);
}

/**
 * The whole calculation:
 *
 *   refund = max(0, held - deductions)
 *   excess = max(0, deductions - held)
 *
 * `held`, not `expected` — the calculator works from the money actually
 * collected. Withholding against a deposit that was never fully paid would
 * charge the tenant twice for the same shortfall.
 */
export function computeRefund(input: {
  heldCents: number;
  deductions: DeductionInput[];
}): RefundMath {
  const held = Math.max(0, cents(input.heldCents));
  const deductionsCents = sumDeductionCents(input.deductions);
  return {
    deductionsCents,
    refundCents: Math.max(0, held - deductionsCents),
    excessCents: Math.max(0, deductionsCents - held),
    overDeducted: deductionsCents > held,
  };
}

/**
 * Is the tenant short on their deposit? Distinct from a deduction: this is
 * money never collected, so it caps what can be withheld and is chased
 * separately.
 */
export function resolveShortfall(input: {
  heldCents: number;
  expectedCents: number;
}): { hasShortfall: boolean; shortfallCents: number } {
  const held = Math.max(0, cents(input.heldCents));
  const expected = Math.max(0, cents(input.expectedCents));
  const shortfallCents = Math.max(0, expected - held);
  return { hasShortfall: shortfallCents > 0, shortfallCents };
}

/* ══════════════════════════════════════════════════════════════════════════ */
/* validation — mirrors the DB constraints                                    */
/* ══════════════════════════════════════════════════════════════════════════ */

export type DeductionProblem =
  | "amount_missing"
  | "amount_not_positive"
  | "category_invalid"
  | "reason_missing"
  | "reason_too_short";

/**
 * Why this deduction cannot be saved. Empty array means it is valid.
 *
 * Returns every problem rather than the first, so a half-filled row reports
 * all of what it needs in one pass instead of one field at a time.
 */
export function validateDeduction(d: DeductionInput): DeductionProblem[] {
  const problems: DeductionProblem[] = [];

  const amount = Number(d?.amount_cents);
  if (d?.amount_cents == null || !Number.isFinite(amount)) {
    problems.push("amount_missing");
  } else if (Math.round(amount) <= 0) {
    problems.push("amount_not_positive");
  }

  if (
    !DEDUCTION_CATEGORIES.includes(
      String(d?.category ?? "") as DeductionCategory
    )
  ) {
    problems.push("category_invalid");
  }

  const reason = String(d?.reason ?? "").trim();
  if (!reason) problems.push("reason_missing");
  else if (reason.length < REASON_MIN_LENGTH) problems.push("reason_too_short");

  return problems;
}

export function isDeductionValid(d: DeductionInput): boolean {
  return validateDeduction(d).length === 0;
}

export type ReleaseProblem =
  | "partial_needs_deduction"
  | "full_has_deductions"
  | "none_needs_deduction"
  | "deduction_invalid"
  | "no_decision";

/**
 * Can this settlement be released? Mirrors deposit_settlement_recompute() so
 * the submit button can be disabled instead of failing at the database.
 */
export function validateRelease(input: {
  decision?: SettlementDecision | string | null;
  deductions: DeductionInput[];
}): ReleaseProblem[] {
  const problems: ReleaseProblem[] = [];
  const list = input.deductions || [];
  const decision = input.decision;

  if (decision !== "full" && decision !== "partial" && decision !== "none") {
    problems.push("no_decision");
  }
  if (list.some((d) => !isDeductionValid(d))) {
    problems.push("deduction_invalid");
  }
  if (decision === "partial" && list.length === 0) {
    problems.push("partial_needs_deduction");
  }
  if (decision === "full" && list.length > 0) {
    problems.push("full_has_deductions");
  }
  // Withholding everything still has to be explained line by line.
  if (decision === "none" && list.length === 0) {
    problems.push("none_needs_deduction");
  }

  return problems;
}

export function canRelease(input: {
  decision?: SettlementDecision | string | null;
  deductions: DeductionInput[];
}): boolean {
  return validateRelease(input).length === 0;
}

/* ══════════════════════════════════════════════════════════════════════════ */
/* bundle readiness                                                           */
/* ══════════════════════════════════════════════════════════════════════════ */

export type BundleDocKind =
  "contract" | "move_in_protocol" | "move_out_protocol";

export interface BundleDoc {
  kind: BundleDocKind;
  ready: boolean;
}

export interface BundleReadiness {
  docs: BundleDoc[];
  ready: boolean;
  /** Which documents are missing — what the blocked card lists. */
  missing: BundleDocKind[];
}

/**
 * Can the bundle be assembled?
 *
 * The bundle does not send while a document is missing. A partial bundle would
 * let the representative decide on the deposit without seeing the walkthrough
 * the decision rests on — so an incomplete send is worse than a late one, and
 * the tick retries instead.
 *
 * The common gap is the move-out protocol PDF: it is generated client-side and
 * only uploaded when the tenant reaches the final step, so an abandoned
 * protocol leaves pdf_storage_path null with nothing for the server to bundle.
 */
export function resolveBundleReadiness(input: {
  /** bookings.signed_contract_url or contract_pdf_url. */
  contractUrl?: string | null;
  /** handover_protocols.pdf_storage_path where kind = 'move_in'. */
  moveInProtocolPdfPath?: string | null;
  /** handover_protocols.pdf_storage_path where kind = 'move_out'. */
  moveOutProtocolPdfPath?: string | null;
}): BundleReadiness {
  const nonEmpty = (v: unknown) => v != null && String(v).trim().length > 0;

  const docs: BundleDoc[] = [
    { kind: "contract", ready: nonEmpty(input.contractUrl) },
    {
      kind: "move_in_protocol",
      ready: nonEmpty(input.moveInProtocolPdfPath),
    },
    {
      kind: "move_out_protocol",
      ready: nonEmpty(input.moveOutProtocolPdfPath),
    },
  ];

  const missing = docs.filter((d) => !d.ready).map((d) => d.kind);
  return { docs, ready: missing.length === 0, missing };
}

/**
 * Should the bundle be attached to the email, or linked?
 *
 * A missing size is treated as too large: guessing small on an unknown
 * photo-heavy protocol risks a bounce, and a link always works.
 */
export function shouldAttachBundle(sizeBytes?: number | null): boolean {
  const size = Number(sizeBytes);
  if (!Number.isFinite(size) || size <= 0) return false;
  return size <= BUNDLE_ATTACH_MAX_BYTES;
}

/* ══════════════════════════════════════════════════════════════════════════ */
/* card state                                                                 */
/* ══════════════════════════════════════════════════════════════════════════ */

export type DepositReturnState =
  /** Move-out date not confirmed yet — the section is not shown. */
  | "not_applicable"
  /** Waiting for the day-after-move-out send. */
  | "scheduled"
  /** A document is missing, so the send cannot proceed. */
  | "blocked"
  /** Bundle assembled and reviewable, not yet sent to the representative. */
  | "prepared"
  /** Bundle delivered, waiting on the representative. */
  | "awaiting_review"
  /** Decision made, refund not yet transferred. */
  | "released_unpaid"
  /** Refund paid. Done. */
  | "settled";

/**
 * Which state the deposit return card renders.
 *
 * Derived from evidence — a stored `status` is only a claim, the same way
 * bookings.status is (see tenantJourney.ts). Timestamps are the truth, so a
 * settlement whose status says 'closed' with no refund_paid_at still reads as
 * released_unpaid.
 */
export function resolveDepositReturnState(input: {
  /** Has the move-out date been confirmed? */
  moveOutDateConfirmed: boolean;
  settlement?: SettlementRow | null;
  readiness?: BundleReadiness | null;
}): DepositReturnState {
  if (!input.moveOutDateConfirmed) return "not_applicable";

  const s = input.settlement;

  if (s?.refund_paid_at) return "settled";
  if (s?.released_at) return "released_unpaid";
  if (s?.bundle_sent_at) return "awaiting_review";

  // Assembled but not sent. Checked before the blocked branches on purpose: a
  // file that exists is reviewable, and a protocol PDF deleted after assembly
  // must not hide a bundle that is sitting there ready to go.
  if (s?.bundle_pdf_path) return "prepared";

  // Not assembled yet: blocked if a document is missing, or if the tick already
  // recorded why it could not proceed.
  //
  // `bundle_blocked_reason` is only cleared server-side on the next successful
  // prepare, so on its own it outlives the problem: an admin who supplies the
  // missing protocol watches the card keep naming a document that is now on
  // file, with no way to clear it but a "Prepare bundle" click the blocked
  // state discourages. Readiness is live, so let it win — but only when it is
  // affirmatively ready (`!== true`, not `!== false`), so a caller that passes
  // no readiness at all still trusts the recorded reason.
  if (s?.bundle_blocked_reason && input.readiness?.ready !== true)
    return "blocked";
  if (input.readiness && !input.readiness.ready) return "blocked";

  return "scheduled";
}

/**
 * Does this tenancy have no deposit to settle at all?
 *
 * The `moved_out` journey gate needs it: a zero-deposit tenancy would otherwise
 * sit forever in "Deposit refund pending", a stage it can never leave.
 *
 * Defined once because the tenant list and the tenant profile both feed the same
 * resolver, and two definitions of "nothing to refund" would put the same tenancy
 * at different stages on the two surfaces.
 *
 * Once a settlement has frozen its snapshot, that is the truth. Before then the
 * contractual deposit is the only reliable signal — deliberately NOT "nothing has
 * been paid in yet", because a tenant who owed a deposit and never paid it must
 * not skip the stage. That case needs an admin to look at it, and the card's
 * shortfall warning is where they will.
 */
export function resolveNoDepositHeld(input: {
  /** Frozen snapshot from the settlement, if one exists. */
  snapshotAt?: string | null;
  snapshotHeldCents?: number | null;
  /** getExpectedDepositEUR(booking) * 100 — the contractual amount. */
  expectedCents: number;
}): boolean {
  if (input.snapshotAt) return (input.snapshotHeldCents ?? 0) === 0;
  return Math.max(0, cents(input.expectedCents)) === 0;
}

/** Days a representative has before the first chase. */
export const REVIEW_REMINDER_DAYS = 7;
/** Days before it escalates past the representative. */
export const REVIEW_ESCALATION_DAYS = 14;

/**
 * Whole days elapsed since an ISO timestamp, or null if absent/unparseable.
 * Used for the reminder thresholds, so it floors rather than rounds — day 6.9
 * is not yet day 7.
 */
export function daysSince(
  iso?: string | null,
  now: Date = new Date()
): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  const ms = now.getTime() - then;
  if (ms < 0) return 0;
  return Math.floor(ms / 86_400_000);
}

/* ──────────────────────── proof of the transfer ─────────────────────────── */

/**
 * What counts as proof that the refund was transferred.
 *
 * Same three types the admin Documents tab and the record-protocol dialog accept,
 * and the same 20 MB ceiling: a bank confirmation is a PDF or a screenshot of
 * one, and accepting more formats than the surfaces that display them produces a
 * file nobody downstream can open. HEIC is deliberately out for that reason — an
 * iPhone screenshot saved as HEIC would upload and then render nowhere.
 */
export const REFUND_PROOF_ACCEPTED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
] as const;

export const REFUND_PROOF_ACCEPT_ATTR = REFUND_PROOF_ACCEPTED_TYPES.join(",");

/** 20 MB, matching every other upload limit in the admin surfaces. */
export const REFUND_PROOF_MAX_BYTES = 20 * 1024 * 1024;

/**
 * Why this file cannot be the proof, or null if it can.
 *
 * Returns the sentence to show rather than a code: there is one caller, the
 * message is the whole point, and a code would only be expanded back into this
 * same string at the call site.
 */
export function validateRefundProof(
  file: { name: string; type: string; size: number } | null | undefined
): string | null {
  if (!file) return "Attach proof of the transaction before confirming.";
  if (file.size > REFUND_PROOF_MAX_BYTES) {
    return "That file is larger than 20 MB — attach a smaller copy.";
  }
  const type = (file.type || "").toLowerCase();
  const byType = (REFUND_PROOF_ACCEPTED_TYPES as readonly string[]).includes(
    type
  );
  // Browsers report an empty MIME type for some files, so the extension is the
  // fallback rather than the primary test.
  const byName = /\.(pdf|jpe?g|png)$/i.test(file.name);
  if (!byType && !byName) {
    return "Attach a PDF, JPG or PNG — that file type cannot be stored as proof.";
  }
  return null;
}

/**
 * Where the move-out bundle stands, for anything that edits a protocol the
 * bundle embeds. The bundle holds the protocol PDF as it was when assembled, so
 * a replacement reaches it only if it is rebuilt — and the rebuild is refused
 * once the bundle has been sent or the deposit released
 * (`prepareMoveoutBundle` returns already_sent / already_released). Either
 * column therefore means "can no longer be rebuilt": `released_at` is not tied
 * to `bundle_sent_at` in the database, and an admin can release first.
 */
export function bundleStateOf(
  s:
    | Pick<
        SettlementRow,
        "bundle_generated_at" | "bundle_sent_at" | "released_at"
      >
    | null
    | undefined
): "none" | "prepared" | "sent" {
  if (s?.released_at || s?.bundle_sent_at) return "sent";
  if (s?.bundle_generated_at) return "prepared";
  return "none";
}

/**
 * Where a proof file is stored, under the settlement it belongs to.
 *
 * Timestamped rather than overwritten: a re-uploaded proof after a correction
 * must not silently replace the file an earlier refund was recorded against, and
 * the settlement row points at exactly one path. The name is flattened because
 * storage keys reject much of what a bank names a download.
 */
export function refundProofPath(input: {
  settlementId: string;
  fileName: string;
  nowIso: string;
}): string {
  const safe =
    input.fileName
      .toLowerCase()
      .replace(/[^a-z0-9.]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(-64) || "proof";
  const stamp = input.nowIso.replace(/[^0-9]/g, "").slice(0, 14);
  return `refund-proofs/${input.settlementId}/${stamp}-${safe}`;
}
